/* Owl Village: a stationary, repeating background with scroll-linked color. */
(() => {
  'use strict';

  function opacityAt(scroll, start, height, viewport) {
    const distance = Math.max(1, height - viewport);
    const progress = Math.max(0, Math.min(1, (scroll - start) / distance));
    return 0.24 + 0.36 * progress;
  }

  function tilesFor(top, height, tileHeight) {
    const first = Math.floor(top / tileHeight);
    const last = Math.ceil((top + height) / tileHeight) - 1;
    return Array.from({ length: Math.max(0, last - first + 1) }, (_, i) => {
      const index = first + i;
      return { index, scene: ((index % 3) + 3) % 3, top: index * tileHeight - top };
    });
  }

  // Pure coordinate helpers can also be checked without a browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { opacityAt, tilesFor };
    return;
  }

  const config = document.currentScript || document.querySelector('script[data-ov-trail]');
  if (!config) return;
  const sources = ['woodland', 'orchard', 'village'].map(key => config.dataset[key]);
  const mobileSources = ['woodland', 'orchard', 'village'].map((key, index) => config.dataset[`${key}Mobile`] || sources[index]);
  const closingDetail = config.dataset.villageClosing;
  if (sources.some(source => !source)) return;
  const instances = new Map();
  const copySelector = [
    'h1', 'h2', 'h3', 'p', 'blockquote', 'label', '.actions', '.owner-link',
    '.text-link', '.editorial-label', '.ov-belief-notes', '.ov-person-link',
    '.hd-video-controls', '.hd-rating', '.resource-form', '.hd-social-footer', '.hd-ribbon-window'
  ].join(',');

  function welcome(root) {
    const greeting = root.querySelector('.ov-curious');
    const path = greeting?.querySelector('.ov-curious__motion');
    const star = greeting?.querySelector('.ov-curious__star');
    if (!path || !star || greeting.dataset.played) return;
    greeting.dataset.played = 'true';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const length = path.getTotalLength();
    const flight = greeting.querySelector('.ov-curious__flight');
    const words = greeting.querySelector('.ov-curious__text');
    const heading = root.querySelector('.hero .hd-hero-heading');
    // CSS may already have painted part of the entrance before this deferred
    // script arrives. Continue that clock; restarting at zero caused a flash
    // and a second eyebrow fade on slower phones.
    const elapsedOf = element => Math.max(0, ...(element?.getAnimations?.() || [])
      .filter(animation => animation.animationName === 'ov-curious-words' || animation.animationName === 'ov-hero-heading')
      .map(animation => Number(animation.currentTime) || 0));
    const inheritedElapsed = Math.max(elapsedOf(words), elapsedOf(heading));
    const start = performance.now() - inheritedElapsed;
    const draw = elapsed => {
      const progress = Math.min(1, elapsed / 1250);
      const point = path.getPointAtLength(length * progress);
      star.setAttribute('transform', `translate(${point.x} ${point.y})`);
      path.setAttribute('stroke-dashoffset', 1 - progress);
      flight.style.opacity = Math.min(1, progress * 10);
      words.style.opacity = Math.min(1, elapsed / 1250);
      if (heading) {
        const reveal = Math.max(0, Math.min(1, (elapsed - 400) / 1500));
        heading.style.opacity = reveal * reveal * (3 - 2 * reveal);
      }
    };
    draw(inheritedElapsed);
    greeting.classList.add('ov-star-active');
    root.classList.add('ov-welcome-active');
    const frame = time => {
      if (!root.isConnected) return;
      const elapsed = time - start;
      draw(elapsed);
      if (elapsed < 1900) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  function mount(root) {
    welcome(root);
    if (instances.has(root) || root.dataset.ovTrailMounted) return;
    root.dataset.ovTrailMounted = 'true';
    const sections = [...root.children].filter(element =>
      element.matches('section, .hd-river, .hd-social-footer')
    );
    const records = sections.map(section => {
      const foreground = [...section.children].filter(element =>
        !element.matches('svg, .ov-belief-waves, script, style')
      );
      section.classList.add('ov-trail-section');
      const positioned = foreground.filter(element => getComputedStyle(element).position === 'static');
      foreground.forEach(element => element.classList.add('ov-trail-foreground'));
      positioned.forEach(element => element.classList.add('ov-trail-positioned'));
      const panel = document.createElement('div');
      panel.className = 'ov-village-trail';
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');
      section.prepend(panel);
      return { section, panel, foreground, positioned };
    });
    let rootTop = 0;
    let rootHeight = 0;
    let scrollFrame = 0;
    let layoutFrame = 0;
    let disposed = false;
    let compact = false;

    function paintOpacity() {
      scrollFrame = 0;
      root.style.setProperty('--ov-trail-opacity', compact ? '0.28' : opacityAt(
        window.scrollY, rootTop, rootHeight, window.innerHeight
      ).toFixed(4));
    }
    function onScroll() {
      if (compact) return;
      if (!scrollFrame) scrollFrame = requestAnimationFrame(paintOpacity);
    }

    function measure() {
      layoutFrame = 0;
      if (disposed || !root.isConnected) return;
      const rootBox = root.getBoundingClientRect();
      rootTop = rootBox.top + window.scrollY;
      rootHeight = rootBox.height;
      const width = rootBox.width;
      const tileHeight = width * 1.5;
      if (!width) return;
      compact = width < 750;
      root.classList.toggle('ov-trail-compact', compact);
      if (compact) {
        // Phones share three small cached assets. Keep the continuous path,
        // without per-copy SVG blur masks or scroll-linked opacity updates.
        records.forEach(({ section, panel }, sectionIndex) => {
          const box = section.getBoundingClientRect();
          const pixel = window.devicePixelRatio || 1;
          const snap = y => Math.round((y + window.scrollY) * pixel) / pixel - window.scrollY;
          const panelTop = snap(box.top);
          const offset = panelTop - rootBox.top;
          const nextTop = records[sectionIndex + 1]?.section.getBoundingClientRect().top ?? rootBox.bottom;
          let panelHeight = snap(Math.max(box.bottom, nextTop)) - panelTop;
          let top = panelTop - box.top;
          let tiles = tilesFor(offset, panelHeight, tileHeight);
          if (section.matches('.newsletter-section')) {
            // Retain the clear 190px illustration space below the invitation.
            panelHeight = Math.min(190, box.height);
            top = box.height - panelHeight;
            tiles = [{ index: 'closing', scene: 2, top: panelHeight - width * 1400 / 1024 }];
          }
          const signature = `mobile:${tiles.map(tile => tile.index).join(',')}`;
          if (panel.dataset.tiles !== signature) {
            panel.replaceChildren(...tiles.map(tile => {
              const img = document.createElement('img');
              Object.assign(img, {src:mobileSources[tile.scene], alt:'', width:640, height:960, decoding:'async', loading:'lazy', draggable:false});
              img.fetchPriority = 'low';
              return img;
            }));
            panel.dataset.tiles = signature;
          }
          const mask = section.matches('.newsletter-section') ? 'none' : 'linear-gradient(to right,rgba(0,0,0,.35),black 85%)';
          panel.style.cssText = `top:${top}px;left:${rootBox.left - box.left}px;width:${width}px;height:${panelHeight}px;mask-image:${mask};-webkit-mask-image:${mask}`;
          [...panel.children].forEach((img, index) => {
            img.style.cssText = `left:0;top:${tiles[index].top}px;width:${width}px;height:${tileHeight + 0.5}px`;
          });
        });
        paintOpacity();
        return;
      }
      // Desktop landmarks follow the content anchors, without moving the sections.
      // Crossfade the quiet road ends so the bridge and farm stay visible across widths.
      const belief = root.querySelector('#our-belief')?.getBoundingClientRect();
      const people = root.querySelector('#our-people')?.getBoundingClientRect();
      const community = root.querySelector('#community')?.getBoundingClientRect();
      const newsletter = root.querySelector('.newsletter-section')?.getBoundingClientRect();
      const closingOwner = newsletter && records.find(({ section }) => section.matches('.hd-social-footer'));
      const closingTop = newsletter ? (width >= 700
        ? newsletter.top - rootBox.top - width * 1040 / 1024
        : rootHeight - width * 1400 / 1024) : null;
      let landmarks = null;
      if (width >= 1000 && belief && people && community) {
        const bridgeTop = belief.top - rootBox.top - width * 860 / 1024 - 70;
        const orchardTop = people.top - rootBox.top - width * 500 / 1024 - 20;
        const join = (bridgeTop + tileHeight + orchardTop) / 2;
        landmarks = { start: community.top - rootBox.top, bridgeTop, orchardTop, join };
      }
      // Share mask coordinates across boundaries, including existing section margins.
      const feather = width < 700 ? 12 : 18;
      const padding = width < 700 ? 8 : 24;
      const clearAreas = [...root.querySelectorAll(copySelector)].filter(element =>
        !element.closest('.product-card, .path-card, .hd-photo-review, .hd-text-review, .hd-video-card, .ov-belief-photo, .hd-social-footer, .newsletter-section')
      ).map(element => {
        const b = element.getBoundingClientRect();
        const pad = padding;
        return { x: b.left - rootBox.left - pad, y: b.top - rootBox.top - pad,
          width: b.width + pad * 2, height: b.height + pad * 2, visible: b.width > 0 && b.height > 0 };
      }).filter(area => area.visible);

      records.forEach(({ section, panel }, sectionIndex) => {
        // One continuous closing layer avoids a raster seam through the scene
        // at the social/newsletter boundary. The later invitation stays above it.
        if (closingOwner && section.matches('.newsletter-section')) {
          panel.style.display = 'none';
          panel.replaceChildren();
          delete panel.dataset.tiles;
          return;
        }
        panel.style.display = 'block';
        const box = section.getBoundingClientRect();
        // Share device-pixel boundaries so independently masked panels do not
        // leave pale fractional-pixel seams between identical art.
        const pixel = window.devicePixelRatio || 1;
        const snap = y => Math.round((y + window.scrollY) * pixel) / pixel - window.scrollY;
        const panelTop = snap(box.top);
        const offset = panelTop - rootBox.top;
        // Include existing margins between sections without changing their layout.
        const nextTop = records[sectionIndex + 1]?.section.getBoundingClientRect().top ?? rootBox.bottom;
        const panelHeight = snap(Math.max(box.bottom, nextTop,
          closingOwner?.section === section ? newsletter.bottom : 0)) - panelTop;
        panel.style.top = `${panelTop - box.top}px`;
        panel.style.left = `${rootBox.left - box.left}px`;
        panel.style.width = `${width}px`;
        panel.style.height = `${panelHeight}px`;
        let tiles = tilesFor(offset, panelHeight, tileHeight);
        if (landmarks && offset + panelHeight > landmarks.start) {
          const { start, bridgeTop, orchardTop, join } = landmarks;
          const fade = 100;
          const custom = [
            { index: 'bridge', scene: 0, globalTop: bridgeTop, from: start - 90, to: join + fade },
            { index: 'orchard', scene: 1, globalTop: orchardTop, from: join - fade, to: orchardTop + tileHeight + fade }
          ];
          for (let index = 0, y = orchardTop + tileHeight; y < rootHeight; index++, y += tileHeight) {
            custom.push({ index: `after-farm-${index}`, scene: (index + 2) % 3, globalTop: y, from: y, to: y + tileHeight });
          }
          tiles = tiles.filter(tile => tile.top + offset < start)
            .map(tile => ({ ...tile, clipEnd: start - offset + 90 }));
          custom.filter(tile => tile.to > offset && tile.from < offset + panelHeight).forEach(tile => {
            tiles.push({ ...tile, top: tile.globalTop - offset, clipStart: tile.from - offset, clipEnd: tile.to - offset });
          });
        }
        // The closing owlets belong beside the invitation, regardless of how
        // much earlier copy wraps. Join in the quiet road at the scene's top.
        if (closingTop != null && offset + panelHeight > closingTop) {
          const join = closingTop + width * .15;
          tiles = tiles.filter(tile => tile.top + offset < join + 90)
            .map(tile => ({ ...tile, clipEnd: Math.min(tile.clipEnd ?? Infinity, join + 90 - offset) }));
          const detailTop = newsletter.top - rootBox.top - width * 0.34;
          tiles.push({ index: 'closing-village', scene: 2, top: closingTop - offset, clipStart: join - 90 - offset,
            ...(closingDetail ? { clipEnd: detailTop + 90 - offset } : {}) });
          if (closingDetail && offset + panelHeight > detailTop) {
            tiles.push({ index: 'closing-detail', source: closingDetail, top: detailTop - offset,
              clipStart: detailTop - offset, clipEnd: rootHeight - offset,
              renderLeft: width * 0.08, renderWidth: width * 0.92, renderHeight: width * 0.92 * 2 / 3 });
          }
        }
        const signature = `desktop:${tiles.map(tile => tile.index).join(',')}`;
        if (panel.dataset.tiles !== signature) {
          panel.replaceChildren(...tiles.map(tile => {
            const img = document.createElement('img');
            img.src = tile.source || sources[tile.scene];
            img.alt = '';
            img.width = tile.source ? 1536 : 1024;
            img.height = tile.source ? 1024 : 1536;
            img.decoding = 'async';
            img.loading = tile.index === 0 ? 'eager' : 'lazy';
            img.fetchPriority = 'low';
            img.draggable = false;
            return img;
          }));
          panel.dataset.tiles = signature;
        }
        [...panel.children].forEach((img, i) => {
          const tile = tiles[i];
          const imageHeight = tile.renderHeight || tileHeight;
          img.style.cssText = `left:${tile.renderLeft || 0}px;top:${tile.top}px;width:${tile.renderWidth || width}px;height:${imageHeight + 0.5}px`;
          if (tile.clipStart != null || tile.clipEnd != null) {
            const first = Math.max(0, (tile.clipStart ?? tile.top) - tile.top);
            const last = Math.min(imageHeight, (tile.clipEnd ?? tile.top + imageHeight) - tile.top);
            const fade = 90;
            const gradient = `linear-gradient(to bottom, transparent ${first}px, black ${first + fade}px, black ${Math.max(first + fade, last - fade)}px, transparent ${last}px)`;
            img.style.maskImage = gradient;
            img.style.webkitMaskImage = gradient;
          }
        });

        // Feather the art away behind readable copy. This changes no content styles.
        const holes = clearAreas.filter(area =>
          area.y + area.height + feather * 3 > offset && area.y - feather * 3 < offset + panelHeight
        ).map(area => `<rect x="${area.x}" y="${area.y - offset}" width="${area.width}" height="${area.height}" rx="12"/>`).join('');
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width + 4}" height="${panelHeight + 4}" viewBox="-2 -2 ${width + 4} ${panelHeight + 4}"><defs><filter id="soft" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="${feather}"/></filter><mask id="clear" maskUnits="userSpaceOnUse" x="-2" y="-2" width="${width + 4}" height="${panelHeight + 4}"><rect x="-2" y="-2" width="${width + 4}" height="${panelHeight + 4}" fill="white"/><g fill="black" filter="url(#soft)">${holes}</g></mask></defs><rect x="-2" y="-2" width="${width + 4}" height="${panelHeight + 4}" fill="white" mask="url(#clear)"/></svg>`;
        const mask = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
        panel.style.maskImage = mask;
        panel.style.webkitMaskImage = mask;
      });
      paintOpacity();
    }
    function scheduleMeasure() {
      if (!layoutFrame && !disposed) layoutFrame = requestAnimationFrame(measure);
    }
    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(root);
    records.forEach(({ section }) => observer.observe(section));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    window.addEventListener('load', scheduleMeasure, { once: true });
    document.fonts?.ready.then(scheduleMeasure);
    scheduleMeasure();

    instances.set(root, () => {
      disposed = true;
      observer.disconnect();
      cancelAnimationFrame(scrollFrame);
      cancelAnimationFrame(layoutFrame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('load', scheduleMeasure);
      records.forEach(({ section, panel, foreground, positioned }) => {
        panel.remove();
        section.classList.remove('ov-trail-section');
        foreground.forEach(element => element.classList.remove('ov-trail-foreground'));
        positioned.forEach(element => element.classList.remove('ov-trail-positioned'));
      });
      root.style.removeProperty('--ov-trail-opacity');
      delete root.dataset.ovTrailMounted;
      instances.delete(root);
    });
  }

  function boot() {
    document.querySelectorAll('.ov-homepage.ov-hadley--a').forEach(mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((dispose, root) => {
      if (event.target.contains(root)) dispose();
    });
  });
})();
